/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { CloudAppConfig } from '@modules/ui-types'
import type Feature from '@modules/utils/feature'

import { setConfig as setConfigAction } from '../actions/config'
import getConfig, { setConfig } from '../lib/configStore'
import { getRawConfig } from '../reducers/config'

import type { ReduxState } from '@/types/redux'
import type { Store } from 'redux'

let _store: Store<ReduxState> | null = null

// Used in test scenarios to utilize mock store with side-car approach of `getReduxState()`
export function setStore(
  store: Store<ReduxState> | null,
  {
    propagateConfig = false,
  }: {
    propagateConfig?: boolean
  } = {},
) {
  _store = store

  // `propagateConfig` is only meant to be set in tests, for easier setup
  if (propagateConfig === false) {
    return
  }

  if (store === null) {
    setConfig({})
    return
  }

  const state = store.getState()
  const config = getRawConfig(state)

  if (config === undefined) {
    return // sanity
  }

  store.dispatch(setConfigAction(config))
}

export function getReduxState(): ReduxState {
  if (_store) {
    return _store.getState()
  }

  // Yes, this isn't a populated state, but it's just to avoid code exploding on null
  return {} as ReduxState
}

export function isFeatureActivated(feature: Feature) {
  return Boolean(getConfigForKey(feature))
}

export function getConfigForKey<K extends Feature | keyof CloudAppConfig>(
  key: K,
): CloudAppConfig[K] | null {
  const appConfig = getConfig()
  return getConfigForKeyFrom(appConfig, key)
}

export function getRequiredConfig<K extends keyof CloudAppConfig>(
  key: K,
): NonNullable<CloudAppConfig[K]> {
  const value = getConfigForKey(key)

  if (value == null) {
    throw new Error('Config key ' + key + ' not defined')
  }

  return value
}

export function getConfigForKeyFrom<K extends Feature | keyof CloudAppConfig>(
  config: CloudAppConfig,
  key: K,
): CloudAppConfig[K] | null {
  if (!config) {
    return null
  }

  const value = config[key]

  if (value === undefined) {
    return null
  }

  return value
}
